import { useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Custom hook for caching and prefetching images
 */
export const useImageCache = () => {
  const queryClient = useQueryClient();

  /**
   * Prefetch and cache an image
   * @param url The image URL to prefetch
   */
  const prefetchImage = (url: string) => {
    if (!url) return;
    
    // Use a consistent query key for the image cache
    const queryKey = ['image', url];
    
    // Only prefetch if not already in cache
    if (!queryClient.getQueryData(queryKey)) {
      queryClient.prefetchQuery({
        queryKey,
        queryFn: () => {
          return new Promise<string>((resolve, reject) => {
            const img = new Image();
            img.src = url;
            img.onload = () => resolve(url);
            img.onerror = (e) => reject(e);
          });
        },
        // Cache for 24 hours
        staleTime: 1000 * 60 * 60 * 24,
        // Keep in cache even when inactive
        gcTime: 1000 * 60 * 60 * 24 * 7,
      });
    }
  };

  /**
   * Prefetch multiple images at once
   * @param urls Array of image URLs to prefetch
   */
  const prefetchImages = (urls: string[]) => {
    if (!urls || !urls.length) return;
    urls.forEach(url => prefetchImage(url));
  };

  /**
   * Load an image with caching
   * @param url The image URL to load
   */
  const useLoadImage = (url: string) => {
    return useQuery({
      queryKey: ['image', url],
      queryFn: () => {
        return new Promise<string>((resolve, reject) => {
          const img = new Image();
          img.src = url;
          img.onload = () => resolve(url);
          img.onerror = (e) => reject(e);
        });
      },
      // Don't refetch on window focus
      refetchOnWindowFocus: false,
      // Cache for 24 hours
      staleTime: 1000 * 60 * 60 * 24,
      // Keep in cache even when inactive
      gcTime: 1000 * 60 * 60 * 24 * 7,
      // Enable the query only if we have a URL
      enabled: !!url,
    });
  };

  return {
    prefetchImage,
    prefetchImages,
    useLoadImage,
  };
};
